import { EditorNodeName } from "@/enums/customAttributes";

import type { EditorNode } from "@/models/TextEditor";

export interface NodePosition {
  index: number;
  offset: number;
}

export const getSerializedNodeLength = (node: EditorNode): number => {
  const isAttribute =
    node.type === EditorNodeName.attribute && node.attributeId;

  if (!isAttribute) {
    return node.content?.length ?? 0;
  }

  const prefix = node.attributeId!.startsWith(":") ? 0 : 1;
  const baseLength = prefix + node.attributeId!.length;
  const hasContext =
    node.context && "value" in node.context && node.context.value;

  if (!hasContext) {
    return baseLength;
  }

  return baseLength + JSON.stringify(node.context).length;
};

const calculateNodeOffset = (
  node: EditorNode,
  rawOffset: number,
  serializedLength: number,
  contentLength: number
): number => {
  if (node.type === EditorNodeName.whitespace) {
    return Math.min(rawOffset, contentLength);
  }

  const isAtomicOrDifferentLength =
    node.atomic || serializedLength !== contentLength;

  if (!isAtomicOrDifferentLength) {
    return Math.min(rawOffset, contentLength);
  }

  if (rawOffset === 0) {
    return 0;
  }

  return contentLength;
};

export const formulaOffsetToNodePosition = (
  nodes: EditorNode[],
  formulaOffset: number
): NodePosition => {
  if (!nodes.length) {
    return { index: 0, offset: 0 };
  }

  let currentOffset = 0;

  for (let nodeIndex = 0; nodeIndex < nodes.length; nodeIndex++) {
    const node = nodes[nodeIndex];
    const serializedLength = getSerializedNodeLength(node);
    const contentLength = node.content?.length ?? 0;

    if (formulaOffset > currentOffset + serializedLength) {
      currentOffset += serializedLength;
      continue;
    }

    const rawOffset = Math.max(0, formulaOffset - currentOffset);
    const nodeOffset = calculateNodeOffset(
      node,
      rawOffset,
      serializedLength,
      contentLength
    );

    return { index: nodeIndex, offset: nodeOffset };
  }

  const lastIndex = nodes.length - 1;
  const lastNode = nodes[lastIndex];

  return {
    index: lastIndex,
    offset: lastNode?.content?.length ?? 0,
  };
};

const calculateFormulaOffsetForNode = (
  targetNode: EditorNode,
  nodeOffset: number,
  serializedLength: number,
  contentLength: number
): number => {
  const isWhitespace = targetNode?.type === EditorNodeName.whitespace;

  if (isWhitespace) {
    return Math.max(0, Math.min(nodeOffset, contentLength));
  }

  const isAttribute =
    targetNode?.type === EditorNodeName.attribute && targetNode.attributeId;

  if (!isAttribute) {
    return Math.max(0, Math.min(nodeOffset, contentLength));
  }

  if (nodeOffset === 0) {
    return 0;
  }

  return serializedLength;
};

export const nodePositionToFormulaOffset = (
  nodes: EditorNode[],
  nodeIndex: number,
  nodeOffset: number
): number => {
  if (!nodes.length) {
    return 0;
  }

  const clampedIndex = Math.max(0, Math.min(nodeIndex, nodes.length - 1));
  let formulaOffset = 0;

  for (let index = 0; index < clampedIndex; index++) {
    formulaOffset += getSerializedNodeLength(nodes[index]);
  }

  const targetNode = nodes[clampedIndex];
  const serializedLength = getSerializedNodeLength(targetNode);
  const contentLength = targetNode?.content?.length ?? 0;

  formulaOffset += calculateFormulaOffsetForNode(
    targetNode,
    nodeOffset,
    serializedLength,
    contentLength
  );

  return formulaOffset;
};

export const getFormulaTextRange = (
  nodes: EditorNode[],
  startNodeIndex: number,
  startOffset: number,
  endNodeIndex: number,
  endOffset: number
): { start: number; end: number } => {
  const start = nodePositionToFormulaOffset(nodes, startNodeIndex, startOffset);
  const end = nodePositionToFormulaOffset(nodes, endNodeIndex, endOffset);

  return { start, end };
};
